import { observer } from "mobx-react";
import moment from "moment";
import * as React from "react";
import { Link, useNavigate, useParams } from "react-router";
import styled from "styled-components";

import DetailsTemplate from "@client/components/templates/DetailsTemplate";
import AlertModal from "@client/components/ui/AlertModal";
import ExportPdfButton from "@client/utils/ExportPdfButton";
import Button from "@client/components/ui/Button";
import { Action as DropdownActionType } from "@client/components/ui/DropdownAction";
import StatusPill from "@client/components/ui/StatusPill";
import Table from "@client/components/ui/Table";
import customerStore from "@client/stores/CustomerStore";
import licenceStore from "@client/stores/LicenceStore";
import userStore from "@client/stores/LicensorStore";
import tagStore from "@client/stores/TagStore";
import { isLocationAvailable } from "@client/utils/Constants";
import Style from "@client/utils/Style";
import { Customer } from "@typings/Customer";
import {
  isSapLicenceVersion,
  LICENCE_VERSION_LABELS,
  LICENCE_VERSION_SAP,
} from "@typings/Licence";
import { Tag } from "@typings/Tag";

import CustomerModal from "../CustomerModal";
import DetailsFields from "./DetailsFields";
import customerActionBarImage from "./images/customer-action-bar.svg";

const Wrapper = styled.div`
  height: 100%;
`;

const ExportButtonWrapper = styled.div`
  position: absolute;
  bottom: 20px;
  right: 20px;
`;

const Content = styled.div``;
const Buttons = styled.div`
  margin-top: 52px;
`;
const LicenceName = styled.div``;
const Pills = styled.div`
  display: flex;
`;
const LinkStyled = styled(Link)`
  color: ${Style.palette.primary};
  text-decoration: none;
`;

type State = {
  showDeleteAlert: boolean;
  showCustomerModal: boolean;
  showDeleteErrorAlert: boolean;
};
type Props = {
  onNavigate: (path: string) => void;
  id: string;
};

@observer
class CustomerDetailsPage extends React.Component<Props, State> {
  state: State = {
    showDeleteAlert: false,
    showCustomerModal: false,
    showDeleteErrorAlert: false,
  };

  get customer(): Customer | undefined {
    return customerStore.customers.find(o => o.id === this.props.id);
  }

  componentDidMount() {
    tagStore.load();
    customerStore.load();
    licenceStore.load();
    if (isLocationAvailable("/licensors", userStore.tokenInfo)) {
      userStore.load();
    }
  }

  getTag(name: string | null): Tag | undefined {
    return tagStore.tags.find(t => t.name === name);
  }

  handleCheckDelete() {
    const customer = this.customer;
    if (!customer) {
      return;
    }
    const licence = licenceStore.licences.find(
      l => l.customer_id === customer?.id,
    );
    if (licence) {
      this.setState({ showDeleteErrorAlert: true });
    } else {
      this.setState({ showDeleteAlert: true });
    }
  }

  async handleDeleteCustomer() {
    this.setState({ showDeleteAlert: false });
    if (!this.customer) {
      return;
    }
    await customerStore.delete(this.customer.id);
    this.props.onNavigate("/customers");
  }

  renderLicencesTable() {
    const customer = this.customer;
    if (!customer) {
      return null;
    }

    const items = licenceStore.licences
      .filter(l => l.customer_id === customer!.id)
      .map(licence => {
        const tag = this.getTag(licence.tag);
        return [
          <LicenceName key={licence.id}>
            <LinkStyled to={`/licences/${licence.id}`}>
              {licence.name || licence.id}
            </LinkStyled>
            <Pills>
              {tag ? (
                <StatusPill
                  label={tag.name}
                  backgroundColor={tag.color}
                  fontColor="white"
                  borderColor={tag.color}
                  style={{ marginTop: "4px" }}
                />
              ) : (
                <StatusPill
                  label="No Tag"
                  backgroundColor="white"
                  fontColor={Style.palette.primary}
                  borderColor={Style.palette.primary}
                  style={{ marginTop: "4px" }}
                />
              )}
              {isSapLicenceVersion(licence.version) ? (
                <StatusPill
                  label={LICENCE_VERSION_LABELS[LICENCE_VERSION_SAP]}
                  backgroundColor={Style.palette.success}
                  fontColor="white"
                  borderColor={Style.palette.success}
                  style={{ marginTop: "4px", marginLeft: "8px" }}
                />
              ) : null}
            </Pills>
          </LicenceName>,
          moment(licence.created_at).format("DD MMM YYYY HH:mm"),
          `${licence.migrations} / ${licence.replicas}`,
          licence.start_date
            ? moment.utc(licence.start_date).format("DD MMM YYYY HH:mm")
            : "-",
          licence.end_date
            ? moment.utc(licence.end_date).format("DD MMM YYYY HH:mm")
            : "-",
          licence.duration || "-",
        ];
      });

    return (
      <Table
        header={[
          "Licences",
          "Created At",
          "Migrations / Replicas",
          "Start Date",
          "End Date",
          "Duration",
        ]}
        noItemsLabel="No licences created for this customer!"
        items={items}
        useSecondaryStyle
        style={{ marginTop: "32px" }}
      />
    );
  }

  renderContent() {
    return (
      <Content>
        <DetailsFields
          customer={this.customer || null}
          tokenInfo={userStore.tokenInfo}
          licences={licenceStore.licences}
          tags={tagStore.tags}
          licensors={userStore.licensors}
        />
        {this.renderLicencesTable()}
        <Buttons>
          <Button
            primary
            onClick={() => {
              this.setState({ showCustomerModal: true });
            }}
          >
            Edit Customer
          </Button>
        </Buttons>
      </Content>
    );
  }

  render() {
    const actions: DropdownActionType[] = [
      {
        label: "Edit Customer",
        color: Style.palette.primary,
        action: () => {
          this.setState({ showCustomerModal: true });
        },
      },
      {
        label: "Delete Customer",
        color: Style.palette.alert,
        action: () => {
          this.handleCheckDelete();
        },
      },
    ];

    const customer = this.customer;
    const columns = [
      { header: "Licences", key: "licences" },
      { header: "Licences Tag", key: "licences_tag" },
      { header: "Created At", key: "created_at" },
      { header: "Migrations / Replicas", key: "migrations_replicas" },
      { header: "Start Date", key: "start_date" },
      { header: "End Date", key: "end_date" },
    ];

    const dataWithIndex = licenceStore.licences
      .filter(l => l.customer_id === customer?.id)
      .map((licence, index) => ({
        index: index + 1,
        licences: licence.name || licence.id,
        licences_tag: licence.tag,
        created_at: moment(licence.created_at).format("DD MMM YYYY HH:mm"),
        migrations_replicas: `${licence.migrations} / ${licence.replicas}`,
        start_date: licence.start_date
          ? moment.utc(licence.start_date).format("DD MMM YYYY HH:mm")
          : "-",
        end_date: licence.end_date
          ? moment.utc(licence.end_date).format("DD MMM YYYY HH:mm")
          : "-",
      }));

    return (
      <Wrapper>
        <DetailsTemplate
          contentComponent={this.renderContent()}
          backLink="/customers"
          dropdownActions={actions}
          icon={customerActionBarImage}
          title={this.customer ? this.customer.name : ""}
          subtitle={(this.customer && this.customer.description) || null}
        />
        <ExportButtonWrapper>
          <ExportPdfButton
            data={dataWithIndex}
            title="Customer Details"
            columns={columns}
          />
        </ExportButtonWrapper>
        {this.state.showDeleteAlert ? (
          <AlertModal
            isOpen
            title="Delete Customer"
            message="Are you sure you want to delete the customer?"
            extraMessage="Deleting a Coriolis Customer is permanent!"
            onConfirmation={() => {
              this.handleDeleteCustomer();
            }}
            onRequestClose={() => {
              this.setState({ showDeleteAlert: false });
            }}
          />
        ) : null}
        {this.state.showCustomerModal ? (
          <CustomerModal
            onRequestClose={() => {
              this.setState({ showCustomerModal: false });
            }}
            customer={this.customer}
          />
        ) : null}
        {this.state.showDeleteErrorAlert ? (
          <AlertModal
            isOpen
            type="error"
            title="Cannot Delete Customer"
            message="The customer has licences associated with him"
            extraMessage="You must first delete the associated licences"
            onRequestClose={() => {
              this.setState({ showDeleteErrorAlert: false });
            }}
          />
        ) : null}
      </Wrapper>
    );
  }
}

function CustomerDetailsPageWithNavigate() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  return <CustomerDetailsPage onNavigate={navigate} id={id!} />;
}

export default CustomerDetailsPageWithNavigate;
